import React, { useState, useEffect, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { videosAPI, artistsAPI, adminUsersAPI, adminVideosAPI } from '../services/api';
import {
  DashboardHeader,
  TabNavigation,
  VideosTable,
  RappersTable,
  VideoModal,
  UsersTable,
  UserModal,
  EditEmailModal,
  AnalyticsDashboard,
  StripeSyncButton,
  RefundSyncButton,
  VideoSalesPanel,
  SalesByVideoView,
} from '../components/AdminDashboard';
import restoreFocusToSalesEntry from '../components/AdminDashboard/sales/restoreFocus';
import ConfirmModal from '../components/common/ConfirmModal';
import AlertModal from '../components/common/AlertModal';

const AdminDashboard = () => {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const [videos, setVideos] = useState([]);
  const [artists, setArtists] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Navigation state lives in the query string so refresh and Back work:
  //   ?tab=<videos|rappers|users|stats>&view=by-video&video=<id>&range=&from=&to=&g=&cur=
  // Switching tabs resets the rest; opening/closing a sales view pushes a history entry.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'videos';
  const salesVideoId = searchParams.get('video');
  const salesView = searchParams.get('view');
  const setActiveTab = (tab) => setSearchParams({ tab });
  const patchParams = (patch) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, String(v))));
      return next;
    });
  const openVideoSales = (video) => patchParams({ video: video.id ?? video });
  const closeVideoSales = () => patchParams({ video: null });
  // When a sales panel closes (Back button or browser Back), return focus to the
  // control that opened it; the panel itself focuses its heading on open.
  const lastSalesVideoId = useRef(salesVideoId);
  useEffect(() => {
    const closedId = lastSalesVideoId.current;
    lastSalesVideoId.current = salesVideoId;
    if (!closedId || salesVideoId) return undefined;
    return restoreFocusToSalesEntry(closedId);
  }, [salesVideoId]);
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingUserEmail, setEditingUserEmail] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'public',
    platform: 'youtube',
    platform_video_id: '',
    thumbnail_url: '',
    price: '',
    artist_ids: [],
    is_battle: false,
    battle_format: null,
    sides: null,
  });
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const [alertModal, setAlertModal] = useState({
    show: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    if (user && isAdmin()) {
      fetchData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [videosRes, artistsRes, usersRes] = await Promise.all([
        videosAPI.getAll({ per_page: 1000, include_unlisted: true }), // admin mgmt: all statuses incl unlisted
        artistsAPI.getAll(),
        adminUsersAPI.getAll({ per_page: 1000 }),
      ]);
      setVideos(videosRes.data);
      setUsers(usersRes.data);

      // Calculate video count for each artist
      const artistsWithVideoCount = artistsRes.data.map(artist => {
        const videoCount = videosRes.data.filter(video =>
          video.artists?.some(a => a.id === artist.id)
        ).length;
        return { ...artist, video_count: videoCount };
      });

      setArtists(artistsWithVideoCount);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Builds the roster portion of the create/update payload: `sides` for a
  // battle (explicit side assignment — see TeamAssignmentModal / the 1v1
  // auto-assignment in VideoModal), `artist_ids` for a plain collaborator
  // list. Matches the backend contract in Api::V1::VideosController.
  const rosterParams = (data) =>
    data.is_battle
      ? { sides: data.sides || {} }
      : { artist_ids: data.artist_ids };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { video: formData, ...rosterParams(formData) };
      if (editingVideo) {
        await videosAPI.update(editingVideo.id, payload);
      } else {
        await videosAPI.create(payload);
      }
      setShowModal(false);
      resetForm();
      fetchData();
      setAlertModal({
        show: true,
        title: 'Success',
        message: editingVideo ? 'Video updated successfully!' : 'Video created successfully!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving video:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: error.response?.data?.errors?.join(', ') || 'Failed to save video',
        type: 'error',
      });
    }
  };

  const handleToggleFeature = async (videoId, currentlyFeatured) => {
    try {
      if (currentlyFeatured) {
        await adminVideosAPI.unfeature(videoId);
      } else {
        await adminVideosAPI.feature(videoId);
      }
      fetchData();
    } catch (error) {
      console.error('Error toggling feature:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update featured status',
        type: 'error',
      });
    }
  };

  // Derives the `sides` shape from a video's side-tagged artists (as
  // returned by the API's artists_with_side serializer) so an existing
  // battle's team assignment is prefilled when the edit modal opens.
  const deriveSidesFromVideo = (video) => {
    if (!video.is_battle) return null;
    const artists = video.artists || [];
    const side1 = artists.filter(a => a.side === 1).map(a => a.id.toString());
    const side2 = artists.filter(a => a.side === 2).map(a => a.id.toString());
    if (side1.length === 0 && side2.length === 0) return null;
    return { 1: side1, 2: side2 };
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      description: video.description || '',
      status: video.status,
      platform: video.platform,
      platform_video_id: video.platform_video_id,
      thumbnail_url: video.thumbnail_url || '',
      price: video.price_cents ? (video.price_cents / 100).toFixed(2) : '',
      artist_ids: video.artists?.map(a => a.id.toString()) || [],
      is_battle: !!video.is_battle,
      battle_format: video.battle_format || null,
      sides: deriveSidesFromVideo(video),
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Video',
      message: 'Are you sure you want to delete this video? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await videosAPI.delete(id);
          fetchData();
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Success',
            message: 'Video deleted successfully!',
            type: 'success',
          });
        } catch (error) {
          console.error('Error deleting video:', error);
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Error',
            message: 'Failed to delete video',
            type: 'error',
          });
        }
      },
    });
  };

  const handleDeleteRapper = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Rapper',
      message: 'Are you sure you want to delete this rapper? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await artistsAPI.delete(id);
          fetchData();
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Success',
            message: 'Rapper deleted successfully!',
            type: 'success',
          });
        } catch (error) {
          console.error('Error deleting rapper:', error);
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Error',
            message: 'Failed to delete rapper',
            type: 'error',
          });
        }
      },
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'public',
      platform: 'youtube',
      platform_video_id: '',
      thumbnail_url: '',
      price: '',
      artist_ids: [],
      is_battle: false,
      battle_format: null,
      sides: null,
    });
    setEditingVideo(null);
  };

  const handleArtistToggle = (artistId) => {
    const idStr = artistId.toString();
    setFormData(prev => ({
      ...prev,
      artist_ids: prev.artist_ids.includes(idStr)
        ? prev.artist_ids.filter(id => id !== idStr)
        : [...prev.artist_ids, idStr]
    }));
  };

  // User Management Handlers
  const handleEditUser = (userToEdit) => {
    setEditingUser(userToEdit);
    setSelectedRole(userToEdit.role);
    setShowUserModal(true);
  };

  const handleSaveUser = async () => {
    try {
      await adminUsersAPI.changeRole(editingUser.id, selectedRole);
      setShowUserModal(false);
      setEditingUser(null);
      setSelectedRole('');
      fetchData();
      setAlertModal({
        show: true,
        title: 'Success',
        message: 'User role updated successfully!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating user:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: error.response?.data?.error || 'Failed to update user role',
        type: 'error',
      });
    }
  };

  const handleDeleteUser = (userId) => {
    setConfirmModal({
      show: true,
      title: 'Delete User',
      message: 'Are you sure you want to delete this user? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await adminUsersAPI.delete(userId);
          fetchData();
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Success',
            message: 'User deleted successfully!',
            type: 'success',
          });
        } catch (error) {
          console.error('Error deleting user:', error);
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
          setAlertModal({
            show: true,
            title: 'Error',
            message: error.response?.data?.error || 'Failed to delete user',
            type: 'error',
          });
        }
      },
    });
  };

  const handleEditUserEmail = (userToEdit) => {
    setEditingUserEmail(userToEdit);
    setShowEmailModal(true);
  };

  const handleSaveEmail = async (newEmail) => {
    try {
      await adminUsersAPI.update(editingUserEmail.id, {
        user: { email: newEmail }
      });
      setShowEmailModal(false);
      setEditingUserEmail(null);
      fetchData();
      setAlertModal({
        show: true,
        title: 'Success',
        message: 'User email updated successfully!',
        type: 'success',
      });
    } catch (error) {
      console.error('Error updating email:', error);
      setAlertModal({
        show: true,
        title: 'Error',
        message: error.response?.data?.error || error.response?.data?.errors?.join(', ') || 'Failed to update email',
        type: 'error',
      });
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user || !isAdmin()) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        <DashboardHeader
          onAddVideo={() => {
            resetForm();
            setShowModal(true);
          }}
        />

        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          videosCount={videos.length}
          rappersCount={artists.length}
          usersCount={users.length}
        />

        {salesVideoId && (
          <VideoSalesPanel key={salesVideoId} videoId={salesVideoId} onClose={closeVideoSales} />
        )}

        {!salesVideoId && activeTab === 'videos' && (
          <>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-4" data-testid="stripe-sync-row">
              <StripeSyncButton className="flex flex-wrap items-center gap-3 min-w-0" />
              <RefundSyncButton className="flex flex-wrap items-center gap-3 min-w-0" />
            </div>
            <VideosTable
              videos={videos}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleFeature={handleToggleFeature}
              onOpenSales={openVideoSales}
            />
          </>
        )}

        {!salesVideoId && activeTab === 'rappers' && (
          <RappersTable
            rappers={artists}
            onDelete={handleDeleteRapper}
          />
        )}

        {!salesVideoId && activeTab === 'users' && (
          <UsersTable
            users={users}
            onEdit={handleEditUser}
            onEditEmail={handleEditUserEmail}
            onDelete={handleDeleteUser}
            currentUserId={user?.id}
          />
        )}

        {!salesVideoId && activeTab === 'stats' && salesView === 'by-video' && (
          <SalesByVideoView
            onSelectVideo={(id) => patchParams({ video: id })}
            onBack={() => patchParams({ view: null })}
          />
        )}

        {!salesVideoId && activeTab === 'stats' && salesView !== 'by-video' && (
          <AnalyticsDashboard
            onNavigate={setActiveTab}
            onOpenSalesByVideo={() => setSearchParams({ tab: 'stats', view: 'by-video' })}
          />
        )}

        <VideoModal
          show={showModal}
          editingVideo={editingVideo}
          formData={formData}
          artists={artists}
          onSubmit={handleSubmit}
          onClose={() => {
            setShowModal(false);
            resetForm();
          }}
          onFormChange={setFormData}
          onArtistToggle={handleArtistToggle}
        />

        <ConfirmModal
          show={confirmModal.show}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null })}
          confirmText="Delete"
        />

        <UserModal
          show={showUserModal}
          user={editingUser}
          selectedRole={selectedRole}
          onRoleChange={setSelectedRole}
          onSave={handleSaveUser}
          onClose={() => {
            setShowUserModal(false);
            setEditingUser(null);
            setSelectedRole('');
          }}
        />

        <EditEmailModal
          show={showEmailModal}
          user={editingUserEmail}
          onSave={handleSaveEmail}
          onClose={() => {
            setShowEmailModal(false);
            setEditingUserEmail(null);
          }}
        />

        <AlertModal
          show={alertModal.show}
          title={alertModal.title}
          message={alertModal.message}
          type={alertModal.type}
          onClose={() => setAlertModal({ show: false, title: '', message: '', type: 'info' })}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;
